import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Package, Key, Wifi, WifiOff, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { adminBoxtalStatus } from "../lib/api";
import { toast } from "sonner";

const AdminBoxtal = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const load = async () => {
    try {
      const s = await adminBoxtalStatus();
      setStatus(s);
    } catch (e) {
      toast.error("Impossible de charger le statut Boxtal");
    } finally {
      setLoading(false);
      setChecking(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runTest = async () => {
    setChecking(true);
    await load();
  };

  const cred = status?.credentials_check || {};
  const ok = cred.ok;

  return (
    <div className="bg-[#050505] min-h-screen text-white">
      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-14 pb-24">
        <button onClick={() => navigate("/admin")}
          data-testid="boxtal-back"
          className="label-caps text-white/50 hover:text-[color:var(--gold)] inline-flex items-center gap-3 mb-10">
          <ArrowLeft size={14} /> Retour
        </button>

        <div className="mb-12">
          <p className="label-caps text-[color:var(--gold)] mb-3">Expéditions · Configuration</p>
          <h1 className="font-serif text-4xl md:text-5xl">
            Connexion <em className="italic text-[color:var(--gold-soft)]">Boxtal</em>
          </h1>
          <p className="text-white/50 font-light mt-3 max-w-2xl">
            Renseignez votre application Boxtal pour générer automatiquement les étiquettes officielles Colissimo, Chronopost, Mondial Relay et Colis Privé.
          </p>
        </div>

        {loading ? (
          <p className="text-white/50">Chargement…</p>
        ) : (
          <div className="space-y-8">
            {/* Étape 1 */}
            <StepCard step="01" title="Application">
              <Row label="Nom de l'application" value={status.app_name || "—"} />
              <Row label="Type d'API" value={status.api_type ? `API ${status.api_type}` : "—"} accent />
              <Row label="Environnement" value={status.env === "prod" ? "Production" : "Test / Sandbox"} />
              <Row label="Base URL" value={status.base_url} mono />
            </StepCard>

            {/* Étape 2 */}
            <StepCard step="02" title="ID de l'application">
              <Row label="Application ID" value={status.app_id || "—"} mono full />
              <p className="text-[11px] text-white/40 mt-3 leading-relaxed">
                Ce champ correspond à l'ID de l'application que vous avez créée sur le portail Boxtal Developer.
                Format attendu : <code className="text-[color:var(--gold-soft)]">app-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx</code>
              </p>
            </StepCard>

            {/* Étape 3 */}
            <StepCard step="03" title="Clé d'accès">
              <Row label="Access Key" value={status.access_key_masked || "—"} mono full />
              <p className="text-[11px] text-white/40 mt-3 leading-relaxed">
                Pour des raisons de sécurité, la clé est masquée. Elle est stockée uniquement côté serveur dans les variables d'environnement.
              </p>
            </StepCard>

            {/* Bouton test */}
            <div className="border border-white/10 p-8 bg-white/[0.02]">
              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                  <p className="label-caps text-[color:var(--gold)] mb-2">Test de connexion</p>
                  <p className="text-sm text-white/60 max-w-md">
                    Vérifie que Boxtal accepte vos identifiants. En cas d'échec, la réponse brute du serveur Boxtal est affichée ci-dessous.
                  </p>
                </div>
                <button
                  onClick={runTest}
                  disabled={checking}
                  data-testid="boxtal-test-connection"
                  className="btn-gold inline-flex items-center gap-2 whitespace-nowrap"
                >
                  {checking ? <RefreshCw size={14} className="animate-spin" /> : <Wifi size={14} />}
                  {checking ? "Test en cours…" : "Tester la connexion"}
                </button>
              </div>

              {/* Result banner */}
              <div className={`mt-6 border p-6 ${ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`}
                data-testid={`boxtal-status-${ok ? "ok" : "fail"}`}
              >
                <div className="flex items-start gap-3">
                  {ok ? (
                    <CheckCircle2 size={22} className="text-emerald-400 mt-0.5" strokeWidth={1.5} />
                  ) : (
                    <AlertTriangle size={22} className="text-red-400 mt-0.5" strokeWidth={1.5} />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`font-serif text-lg mb-1 ${ok ? "text-emerald-300" : "text-red-300"}`}>
                      {ok ? "Connexion Boxtal établie" : "Boxtal a refusé la connexion"}
                    </p>
                    <p className="text-white/70 text-sm break-words">{cred.message || "—"}</p>
                    {cred.http_status ? (
                      <p className="text-[11px] label-caps text-white/40 mt-2">HTTP {cred.http_status}</p>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Detailed attempts */}
              {cred.attempts?.length > 0 && (
                <div className="mt-6">
                  <p className="label-caps text-white/40 text-[10px] mb-3">Détail des tentatives ({cred.attempts.length})</p>
                  <div className="space-y-3">
                    {cred.attempts.map((a, i) => (
                      <details
                        key={i}
                        data-testid={`boxtal-attempt-${i}`}
                        className="border border-white/10 bg-black/30 open:bg-black/50 transition-colors"
                      >
                        <summary className="cursor-pointer px-4 py-3 flex items-center gap-3 text-sm">
                          <span className={`w-2 h-2 rounded-full ${a.ok ? "bg-emerald-400" : "bg-red-400"}`} />
                          <span className="font-mono text-[11px] text-white/50 shrink-0">
                            HTTP {a.http_status ?? "—"}
                          </span>
                          <span className="text-white/80 truncate">{a.label}</span>
                        </summary>
                        <div className="px-4 pb-4 pt-1 space-y-2">
                          <p className="text-[11px] text-white/40 font-mono break-all">→ {a.url}</p>
                          <pre className="text-[11px] leading-relaxed text-white/70 bg-black/60 p-3 border border-white/5 overflow-x-auto whitespace-pre-wrap break-words">
{a.response_body}
                          </pre>
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Help block if failing */}
            {!ok && (
              <div className="border border-[color:var(--gold)]/30 p-8 bg-[color:var(--gold)]/[0.03]">
                <div className="flex items-start gap-3 mb-4">
                  <Key size={18} className="text-[color:var(--gold)] mt-1" strokeWidth={1.4} />
                  <div>
                    <p className="label-caps text-[color:var(--gold)] mb-1">Pourquoi Boxtal refuse-t-il ?</p>
                    <p className="text-white/60 text-sm">
                      Selon la réponse ci-dessus, voici les points à vérifier côté portail Boxtal.
                    </p>
                  </div>
                </div>
                <ol className="space-y-3 text-sm text-white/70 pl-4 list-decimal">
                  <li>Vérifier que l'application est en statut <strong className="text-white">Active</strong> sur <a className="text-[color:var(--gold)] underline" href="https://developer.boxtal.com" target="_blank" rel="noreferrer">developer.boxtal.com</a>.</li>
                  <li>Confirmer que la version d'API cochée dans l'app Boxtal correspond à <strong className="text-white">{status.api_type?.toUpperCase()}</strong>.</li>
                  <li>Contacter <a className="text-[color:var(--gold)] underline" href="https://shipping.boxtal.com/fr/fr/app/contact" target="_blank" rel="noreferrer">l'équipe Boxtal Business</a> pour activer l'application côté API si elle n'est pas encore active.</li>
                  <li>Régénérer la clé d'accès depuis le portail si celle-ci a plus de quelques mois, puis me la redonner.</li>
                </ol>
              </div>
            )}

            {/* Sender info */}
            <StepCard step="04" title="Adresse expéditeur">
              <Row label="Nom" value={status.sender?.name} />
              <Row label="Rue" value={status.sender?.street} />
              <Row label="Code postal" value={status.sender?.postcode} />
              <Row label="Ville" value={status.sender?.city} />
              <Row label="Pays" value={status.sender?.country} />
              <Row label="Téléphone" value={status.sender?.phone} />
              <Row label="Email" value={status.sender?.email} />
              <p className="text-[11px] text-white/40 mt-3">
                Ces informations sont utilisées pour l'enlèvement des colis. Modifiables via les variables <code className="text-[color:var(--gold-soft)]">BOXTAL_SENDER_*</code>.
              </p>
            </StepCard>
          </div>
        )}
      </div>
    </div>
  );
};

const StepCard = ({ step, title, children }) => (
  <div className="border border-white/10 p-8 bg-white/[0.015]" data-testid={`boxtal-step-${step}`}>
    <div className="flex items-baseline gap-4 mb-6">
      <span className="label-caps text-[color:var(--gold)] text-xs tracking-[0.3em]">{step}</span>
      <h2 className="font-serif text-2xl text-white">{title}</h2>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const Row = ({ label, value, mono = false, accent = false, full = false }) => (
  <div className={`flex ${full ? "flex-col gap-2" : "items-baseline justify-between gap-6"} py-2 border-b border-white/5`}>
    <span className="label-caps text-white/40 text-[10px] shrink-0">{label}</span>
    <span
      className={`${mono ? "font-mono text-[13px]" : "text-sm"} ${accent ? "text-[color:var(--gold)]" : "text-white"} ${full ? "" : "text-right"} break-all`}
    >
      {value || "—"}
    </span>
  </div>
);

export default AdminBoxtal;
